import { DsmGrid } from "@dsm-dcs/design-system-react";
import { Dispatch, FC, SetStateAction } from "react";
import ReactHookDsmInput from "../../../../../modules/Helpers/ReactHookDsmInput2";
import { FormType } from "../../common";
import { useIntl } from "../../../../../../_metronic/i18n/customUseIntl";
import { processAndStageStylesV2 } from "../../../../../../_metronic/layout";
import { CSSClassesList } from "../../../../helpers/helperTypes";
import { BeefIntervention } from "../../../../models/Intervention/BeefIntervention";
import { useFormContext } from "react-hook-form";
import { UserProfilePrefs } from "../../../../../modules/Helpers/UserProfilePrefs";
import { unitLong } from "../../../../utils/unit-utils";
import { BeefInputBreeding, BeefInputGrowing } from "../../../../models/Baseline/BeefBaseline";
import { StageType } from "../../../../../../graphql/types";
import EditModeButtons from "../../../helpers/EditModeButtons";
import RowTextFieldWithMetrics from "../../../../../modules/Farms/Intervention/RowControlledTextFieldWithMetrics2";
import { Spacer } from "../../../helpers/Spacer";
import { isFieldFilled } from "../CommonDataParts/InterventionGenericInfoDialog";

interface BeefInputStockComponentProps {
  formType: FormType;
  stageType: StageType;
  fieldItemPrefix: string;
  defaultStageData: BeefInputGrowing | BeefInputBreeding;
  isStockEdit: boolean;
  setIsStockEdit: Dispatch<SetStateAction<boolean>>;
  stockCancelHandler: (isInnerCancel?: boolean) => void;
}

const BeefInputStockComponent: FC<BeefInputStockComponentProps> = ({
  formType,
  stageType,
  fieldItemPrefix,
  defaultStageData,
  isStockEdit,
  setIsStockEdit,
  stockCancelHandler,
}) => {
  const intl = useIntl();
  const classes = processAndStageStylesV2() as CSSClassesList;
  const userUOM = UserProfilePrefs.getInstance().getUserUnitPrefs();
  const fc = useFormContext<BeefIntervention>();
  const isBreedingStage = stageType === StageType.Breeding;

  const renderAverageWeightAtEnd = () => {
    if (isBreedingStage)
      return <div />;

    return isFieldFilled(fc.getValues(`${fieldItemPrefix}.averageWeightAtEnd`)) ? (
      <div>
        <RowTextFieldWithMetrics
          name={`${fieldItemPrefix}.averageWeightAtEnd`}
          label={intl.formatMessage({ id: "SUSTELL.STAGE.BEEF.INPUT.GROWING.AVERAGE_WEIGHT_END" })}
          tooltip={intl.formatMessage({ id: "SUSTELL.STAGE.BEEF.INPUT.GROWING.AVERAGE_WEIGHT_END.TOOLTIP" })}
          baseline={(defaultStageData as BeefInputGrowing).averageWeightAtEnd}
          displayTypeSelect={false}
          type="number"
          metricUnit={intl.formatMessage(
            { id: "SUSTELL.STAGE.BEEF.INPUT.AVERAGE_WEIGHT_START.ADORNMENT" },
            { unit: userUOM?.unitOutputMass ? unitLong(userUOM.unitOutputMass) : "kg" }
          )}
          disabled
        />
        <Spacer />
      </div>
    ) : (
      <ReactHookDsmInput
        name={`${fieldItemPrefix}.originalAverageWeightAtEnd`}
        label={intl.formatMessage({ id: "SUSTELL.STAGE.BEEF.INPUT.GROWING.AVERAGE_WEIGHT_END" })}
        tooltip={intl.formatMessage({ id: "SUSTELL.STAGE.BEEF.INPUT.GROWING.AVERAGE_WEIGHT_END.TOOLTIP" })}
        adornment={intl.formatMessage(
          { id: "SUSTELL.STAGE.BEEF.INPUT.AVERAGE_WEIGHT_START.ADORNMENT" },
          { unit: userUOM?.unitOutputMass ? unitLong(userUOM.unitOutputMass) : "kg" }
        )}
        defaultValue={(defaultStageData as BeefInputGrowing).averageWeightAtEnd}
        type="number"
        disabled
      />
    )
  }

  return (
    <DsmGrid className={classes.dsmGridTwoColumnNoRowSpacing}>
      <p className={classes.headingTitleVariant}>
        {intl.formatMessage({ id: "SUSTELL.STAGE.CATTLE.INPUT.STOCK" })}
      </p>
      <div />
      <p>
        {intl.formatMessage({
          id: "SUSTELL.STAGE.CATTLE.INPUT.STOCK_DESCRIPTION",
        })}
      </p>
      <div />
      {!isStockEdit ? (
        <>
          {isFieldFilled(fc.getValues(`${fieldItemPrefix}.animalsPresentAtStart`)) ? (
            <div>
              <RowTextFieldWithMetrics
                name={`${fieldItemPrefix}.animalsPresentAtStart`}
                label={intl.formatMessage({ id: "SUSTELL.STAGE.BEEF.INPUT.ANIMALS_PRESENT_START" })}
                tooltip={intl.formatMessage({
                  id:
                    isBreedingStage ?
                      "SUSTELL.STAGE.BEEF.INPUT.ANIMALS_PRESENT_START.TOOLTIP" :
                      "SUSTELL.STAGE.BEEF.INPUT.GROWING.ANIMALS_PRESENT_START.TOOLTIP"
                })}
                baseline={defaultStageData.animalsPresentAtStart}
                displayTypeSelect={false}
                type="number"
                metricUnit={intl.formatMessage({ id: "SUSTELL.STAGE.BEEF.INPUT.ANIMALS_PRESENT_START.ADORNMENT" })}
                disabled
              />
              <Spacer />
            </div>
          ) : (
            <ReactHookDsmInput
              name={`${fieldItemPrefix}.originalAnimalsPresentAtStart`}
              label={intl.formatMessage({ id: "SUSTELL.STAGE.BEEF.INPUT.ANIMALS_PRESENT_START" })}
              tooltip={intl.formatMessage({
                id:
                  isBreedingStage ?
                    "SUSTELL.STAGE.BEEF.INPUT.ANIMALS_PRESENT_START.TOOLTIP" :
                    "SUSTELL.STAGE.BEEF.INPUT.GROWING.ANIMALS_PRESENT_START.TOOLTIP"
              })}
              adornment={intl.formatMessage({ id: "SUSTELL.STAGE.BEEF.INPUT.ANIMALS_PRESENT_START.ADORNMENT" })}
              defaultValue={defaultStageData.animalsPresentAtStart}
              type="number"
              disabled
            />
          )}
          {isFieldFilled(fc.getValues(`${fieldItemPrefix}.animalsPresentAtEnd`)) ? (
            <div>
              <RowTextFieldWithMetrics
                name={`${fieldItemPrefix}.animalsPresentAtEnd`}
                label={intl.formatMessage({ id: "SUSTELL.STAGE.BEEF.INPUT.ANIMALS_PRESENT_END" })}
                tooltip={intl.formatMessage({
                  id:
                    isBreedingStage ?
                      "SUSTELL.STAGE.BEEF.INPUT.ANIMALS_PRESENT_END.TOOLTIP" :
                      "SUSTELL.STAGE.BEEF.INPUT.GROWING.ANIMALS_PRESENT_END.TOOLTIP",
                })}
                baseline={defaultStageData.animalsPresentAtEnd}
                displayTypeSelect={false}
                type="number"
                metricUnit={intl.formatMessage({ id: "SUSTELL.STAGE.BEEF.INPUT.ANIMALS_PRESENT_END.ADORNMENT" })}
                disabled
              />
              <Spacer />
            </div>
          ) : (
            <ReactHookDsmInput
              name={`${fieldItemPrefix}.originalAnimalsPresentAtEnd`}
              label={intl.formatMessage({ id: "SUSTELL.STAGE.BEEF.INPUT.ANIMALS_PRESENT_END" })}
              tooltip={intl.formatMessage({
                id:
                  isBreedingStage ?
                    "SUSTELL.STAGE.BEEF.INPUT.ANIMALS_PRESENT_END.TOOLTIP" :
                    "SUSTELL.STAGE.BEEF.INPUT.GROWING.ANIMALS_PRESENT_END.TOOLTIP",
              })}
              adornment={intl.formatMessage({ id: "SUSTELL.STAGE.BEEF.INPUT.ANIMALS_PRESENT_END.ADORNMENT" })}
              defaultValue={defaultStageData.animalsPresentAtEnd}
              type="number"
              disabled
            />
          )}
          {isFieldFilled(fc.getValues(`${fieldItemPrefix}.averageWeightAtStart`)) ? (
            <div>
              <RowTextFieldWithMetrics
                name={`${fieldItemPrefix}.averageWeightAtStart`}
                label={intl.formatMessage({ id: "SUSTELL.STAGE.BEEF.INPUT.GROWING.AVERAGE_WEIGHT_START" })}
                tooltip={intl.formatMessage({
                  id:
                    isBreedingStage ?
                      "SUSTELL.STAGE.BEEF.INPUT.AVERAGE_WEIGHT_START.TOOLTIP" :
                      "SUSTELL.STAGE.BEEF.INPUT.GROWING.AVERAGE_WEIGHT_START.TOOLTIP"
                })}
                baseline={defaultStageData.averageWeightAtStart}
                displayTypeSelect={false}
                type="number"
                metricUnit={intl.formatMessage(
                  { id: "SUSTELL.STAGE.BEEF.INPUT.AVERAGE_WEIGHT_START.ADORNMENT" },
                  { unit: userUOM?.unitOutputMass ? unitLong(userUOM.unitOutputMass) : "kg" }
                )}
                disabled
              />
              <Spacer />
            </div>
          ) : (
            <ReactHookDsmInput
              name={`${fieldItemPrefix}.originalAverageWeightAtStart`}
              label={intl.formatMessage({ id: "SUSTELL.STAGE.BEEF.INPUT.GROWING.AVERAGE_WEIGHT_START" })}
              tooltip={intl.formatMessage({
                id: isBreedingStage ?
                  "SUSTELL.STAGE.BEEF.INPUT.AVERAGE_WEIGHT_START.TOOLTIP" :
                  "SUSTELL.STAGE.BEEF.INPUT.GROWING.AVERAGE_WEIGHT_START.TOOLTIP"
              })}
              adornment={intl.formatMessage(
                { id: "SUSTELL.STAGE.BEEF.INPUT.AVERAGE_WEIGHT_START.ADORNMENT" },
                { unit: userUOM?.unitOutputMass ? unitLong(userUOM.unitOutputMass) : "kg" }
              )}
              defaultValue={defaultStageData.averageWeightAtStart}
              type="number"
              disabled
            />
          )}
          {renderAverageWeightAtEnd()}
        </>
      ) : (
        <>
          <div>
            <RowTextFieldWithMetrics
              name={`${fieldItemPrefix}.animalsPresentAtStart`}
              label={intl.formatMessage({ id: "SUSTELL.STAGE.BEEF.INPUT.ANIMALS_PRESENT_START" })}
              tooltip={intl.formatMessage({
                id:
                  isBreedingStage ?
                    "SUSTELL.STAGE.BEEF.INPUT.ANIMALS_PRESENT_START.TOOLTIP" :
                    "SUSTELL.STAGE.BEEF.INPUT.GROWING.ANIMALS_PRESENT_START.TOOLTIP"
              })}
              baseline={defaultStageData.animalsPresentAtStart}
              type="number"
              metricUnit={intl.formatMessage({ id: "SUSTELL.STAGE.BEEF.INPUT.ANIMALS_PRESENT_START.ADORNMENT" })}
              disabled={false}
            />
            <Spacer />
          </div>
          <div>
            <RowTextFieldWithMetrics
              name={`${fieldItemPrefix}.animalsPresentAtEnd`}
              label={intl.formatMessage({ id: "SUSTELL.STAGE.BEEF.INPUT.ANIMALS_PRESENT_END" })}
              tooltip={intl.formatMessage({
                id:
                  isBreedingStage ?
                    "SUSTELL.STAGE.BEEF.INPUT.ANIMALS_PRESENT_END.TOOLTIP" :
                    "SUSTELL.STAGE.BEEF.INPUT.GROWING.ANIMALS_PRESENT_END.TOOLTIP",
              })}
              baseline={defaultStageData.animalsPresentAtEnd}
              type="number"
              metricUnit={intl.formatMessage({ id: "SUSTELL.STAGE.BEEF.INPUT.ANIMALS_PRESENT_END.ADORNMENT" })}
              disabled={false}
            />
            <Spacer />
          </div>
          <div>
            <RowTextFieldWithMetrics
              name={`${fieldItemPrefix}.averageWeightAtStart`}
              label={intl.formatMessage({ id: "SUSTELL.STAGE.BEEF.INPUT.GROWING.AVERAGE_WEIGHT_START" })}
              tooltip={intl.formatMessage({
                id: isBreedingStage ?
                  "SUSTELL.STAGE.BEEF.INPUT.AVERAGE_WEIGHT_START.TOOLTIP" :
                  "SUSTELL.STAGE.BEEF.INPUT.GROWING.AVERAGE_WEIGHT_START.TOOLTIP"
              })}
              baseline={defaultStageData.averageWeightAtStart}
              type="number"
              metricUnit={intl.formatMessage(
                { id: "SUSTELL.STAGE.BEEF.INPUT.AVERAGE_WEIGHT_START.ADORNMENT" },
                { unit: userUOM?.unitOutputMass ? unitLong(userUOM.unitOutputMass) : "kg" }
              )}
              disabled={false}
            />
            <Spacer />
          </div>
          {!isBreedingStage ? (
            <div>
              <RowTextFieldWithMetrics
                name={`${fieldItemPrefix}.averageWeightAtEnd`}
                label={intl.formatMessage({ id: "SUSTELL.STAGE.BEEF.INPUT.GROWING.AVERAGE_WEIGHT_END" })}
                tooltip={intl.formatMessage({ id: "SUSTELL.STAGE.BEEF.INPUT.GROWING.AVERAGE_WEIGHT_END.TOOLTIP" })}
                baseline={(defaultStageData as BeefInputGrowing).averageWeightAtEnd}
                type="number"
                metricUnit={intl.formatMessage(
                  { id: "SUSTELL.STAGE.BEEF.INPUT.AVERAGE_WEIGHT_START.ADORNMENT" },
                  { unit: userUOM?.unitOutputMass ? unitLong(userUOM.unitOutputMass) : "kg" }
                )}
                disabled={false}
              />
              <Spacer />
            </div>
          ) : (
            <div />
          )}
        </>
      )}
      {formType !== FormType.View &&
        <EditModeButtons
          isEditing={isStockEdit}
          onEdit={() => setIsStockEdit(true)}
          onCancel={stockCancelHandler}
        />}
    </DsmGrid>
  )
}

export default BeefInputStockComponent;
